// src/components/widgets/HeadlessWidgetEditor.jsx
import React, { useState, useEffect } from 'react';
import {
  Settings,
  Package,
  FileText,
  MessageSquare,
  Code,
  Copy,
  Check,
  Power,
  Edit3,
  Trash2,
  Plus,
  X
} from 'lucide-react';

// shadcn components
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { Badge } from '../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';

import useWidgets from '../../hooks/useWidgets';
import { useSettings } from '../../hooks/useSettings';
import QuestionFormDialog from './QuestionFormDialog';

const HeadlessWidgetEditor = ({ widgetId }) => {
  const {
    selectedWidget,
    editing,
    startEditing,
    updateEditingDraft,
    stopEditing,
    toggleWidgetStatus,
    savingWidget
  } = useWidgets();

  const { services } = useSettings();

  const [copiedApiKey, setCopiedApiKey] = useState(false);
  const [copiedWidgetId, setCopiedWidgetId] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);

  // Start editing session when widget is selected
  useEffect(() => {
    if (selectedWidget?.id) {
      startEditing(selectedWidget.id);
    }

    // Cleanup: stop editing when component unmounts or widget changes
    return () => {
      if (editing?.widgetId) {
        stopEditing();
      }
    };
  }, [selectedWidget?.id]);

  // Handle configuration field changes (updates fields inside configuration object)
  const handleConfigChange = (field, value) => {
    if (!editing?.draft) return;

    updateEditingDraft({
      configuration: {
        ...editing.draft.configuration,
        [field]: value
      }
    });
  };

  // Handle service selection toggle
  const handleServiceToggle = (serviceId) => {
    if (!editing?.draft) return;

    const currentEnabled = editing.draft.configuration?.enabled_services || [];
    const newEnabled = currentEnabled.includes(serviceId)
      ? currentEnabled.filter(id => id !== serviceId)
      : [...currentEnabled, serviceId];

    handleConfigChange('enabled_services', newEnabled);
  };

  // Handle field visibility toggles
  const handleFieldVisibilityToggle = (field) => {
    if (!editing?.draft) return;

    const currentValue = editing.draft.configuration?.[field] || false;
    handleConfigChange(field, !currentValue);
  };

  // Handle custom questions
  const handleAddQuestion = () => {
    setEditingQuestion(null);
    setIsDialogOpen(true);
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    setIsDialogOpen(true);
  };

  const handleSaveQuestion = (questionData) => {
    if (!editing?.draft) return;

    const currentQuestions = editing.draft.configuration?.custom_questions || [];

    if (editingQuestion) {
      // Update existing question
      const updatedQuestions = currentQuestions.map(q =>
        q.id === questionData.id ? questionData : q
      );
      handleConfigChange('custom_questions', updatedQuestions);
    } else {
      // Add new question
      handleConfigChange('custom_questions', [...currentQuestions, questionData]);
    }
  };

  const handleDeleteQuestion = (questionId) => {
    if (!editing?.draft) return;

    if (confirm('Are you sure you want to delete this custom field?')) {
      const currentQuestions = editing.draft.configuration?.custom_questions || [];
      handleConfigChange('custom_questions', currentQuestions.filter(q => q.id !== questionId));
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text, setCopied) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Helper functions for table display
  const getQuestionTypeLabel = (type) => {
    const labels = {
      text: 'Short Text',
      textarea: 'Long Text',
      select: 'Dropdown',
      radio: 'Radio',
      checkbox: 'Checkbox'
    };
    return labels[type] || type;
  };

  const formatOptions = (question) => {
    if (!question.options || question.options.length === 0) return '—';
    if (question.options.length <= 3) {
      return question.options.join(', ');
    }
    return `${question.options.slice(0, 2).join(', ')}... (${question.options.length} total)`;
  };

  const formatValidation = (question) => {
    if (!question.validation) return '—';
    const { min_length, max_length } = question.validation;
    if (!min_length && !max_length) return '—';
    if (min_length && max_length) return `${min_length}-${max_length} chars`;
    if (min_length) return `Min ${min_length} chars`;
    if (max_length) return `Max ${max_length} chars`;
    return '—';
  };

  const getStepLabel = (step) => {
    return step === 'service' ? 'Service' : 'Contact';
  };

  // Filter services - only show active services
  const activeServices = services?.filter(service => service.active) || [];

  const enabledServicesCount = (editing.draft?.configuration?.enabled_services || []).length || activeServices.length;
  const customQuestionsCount = (editing.draft?.configuration?.custom_questions || []).length;

  if (!selectedWidget) {
    return (
      <div className="h-full flex items-center justify-center bg-muted/20">
        <div className="text-center p-8">
          <Settings className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Select a Widget</h3>
          <p className="text-muted-foreground text-sm">
            Choose a widget from the list to configure
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gradient-to-tl from-[#0a0a0a] from-0% via-[#0b0b0f] via-20% via-[#0c0c18] via-40% via-[#0b0b12] via-60% via-[#0b0b0e] via-80% to-[#0a0a0a] to-100%">
      {/* Main Content - Scrollable */}
      <div className="flex-1 overflow-y-auto pt-20 pl-36 pr-36 pb-20 space-y-10">
        {/* Header */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <h1 className="text-4xl font-bold text-foreground">
              {selectedWidget.widget_name}
            </h1>
            <Badge variant="secondary" className="text-sm">Headless</Badge>
            <Badge variant={selectedWidget.is_active ? "default" : "secondary"} className="text-sm">
              {selectedWidget.is_active ? "Active" : "Inactive"}
            </Badge>
          </div>

          {/* Gradient Underline */}
          <div className="h-0.5 w-full bg-gradient-to-r from-primary/50 to-transparent" />
        </div>

        {/* Widget Status Section */}
        <div className="space-y-5">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
              <Power className="w-5 h-5" />
              Widget Status
            </h3>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              Control whether this widget is available for use. Inactive widgets can still be configured but won't accept bookings.
            </p>
          </div>

          <div className="flex items-center justify-between p-4 bg-muted/10 rounded-lg border border-border/50">
            <div>
              <Label className="text-base font-medium">Active Status</Label>
              <p className="text-sm text-muted-foreground mt-1">
                Make this widget available for public use
              </p>
            </div>
            <Switch
              checked={selectedWidget.is_active}
              onCheckedChange={(checked) => toggleWidgetStatus(selectedWidget.id, checked)}
              disabled={savingWidget}
            />
          </div>
        </div>

        <Separator />

        {/* Available Services Section */}
        <div className="space-y-5">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
              <Package className="w-5 h-5" />
              Available Services
              {enabledServicesCount > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-primary/20 text-primary text-xs rounded-full font-normal">
                  {enabledServicesCount}
                </span>
              )}
            </h3>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              Select which services customers can book through this widget. If no services are selected, all active services will be available.
            </p>
          </div>

          {/* Service List */}
          <div className="space-y-3">
            {activeServices.length === 0 ? (
              <div className="text-center py-12 bg-muted/10 rounded-lg border border-border/50">
                <Package className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">
                  No active services available
                </p>
              </div>
            ) : (
              activeServices.map((service) => {
                const isEnabled = (editing.draft?.configuration?.enabled_services || []).includes(service.id) ||
                                 (editing.draft?.configuration?.enabled_services || []).length === 0;

                return (
                  <div
                    key={service.id}
                    className="flex items-start gap-3 p-4 bg-muted/10 rounded-lg border border-border/50 hover:bg-muted/20 transition-colors cursor-pointer"
                    onClick={() => handleServiceToggle(service.id)}
                  >
                    <input
                      type="checkbox"
                      checked={isEnabled}
                      onChange={() => handleServiceToggle(service.id)}
                      className="mt-1 rounded border-gray-300 cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: service.color_code }}
                        />
                        <p className="font-medium">{service.name}</p>
                      </div>
                      {service.description && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {service.description}
                        </p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        {service.base_price && (
                          <span>${service.base_price}</span>
                        )}
                        {service.base_duration && (
                          <span>{service.base_duration} min</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <Separator />

        {/* Data Collection Section */}
        <div className="space-y-5">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
              <FileText className="w-5 h-5" />
              Data Collection
            </h3>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              Define what customer data this widget should collect and store. Name, email, and phone are always required. Enable additional fields as needed for your booking requirements.
            </p>
          </div>

          {/* Required Fields Info */}
          <div className="p-4 bg-muted/10 rounded-lg border border-border/50">
            <p className="text-sm font-semibold mb-2">Required Fields (Always Collected)</p>
            <ul className="text-sm space-y-1 text-muted-foreground">
              <li>• Customer Name</li>
              <li>• Email Address</li>
              <li>• Phone Number</li>
            </ul>
          </div>

          {/* Optional Data Fields */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold">Optional Data Fields</Label>

            <div className="flex items-center justify-between p-4 bg-muted/10 rounded-lg border border-border/50">
              <div>
                <Label className="font-medium">Customer Address</Label>
                <p className="text-xs text-muted-foreground mt-1">Collect and store service location address</p>
              </div>
              <Switch
                checked={editing.draft?.configuration?.show_address || false}
                onCheckedChange={() => handleFieldVisibilityToggle('show_address')}
              />
            </div>

            <div className="flex items-center justify-between p-4 bg-muted/10 rounded-lg border border-border/50">
              <div>
                <Label className="font-medium">Booking Notes</Label>
                <p className="text-xs text-muted-foreground mt-1">Allow customers to provide additional details</p>
              </div>
              <Switch
                checked={editing.draft?.configuration?.show_notes !== false}
                onCheckedChange={() => handleFieldVisibilityToggle('show_notes')}
              />
            </div>

            <div className="flex items-center justify-between p-4 bg-muted/10 rounded-lg border border-border/50">
              <div>
                <Label className="font-medium">Referral Source</Label>
                <p className="text-xs text-muted-foreground mt-1">Track how customers found your business</p>
              </div>
              <Switch
                checked={editing.draft?.configuration?.show_how_heard !== false}
                onCheckedChange={() => handleFieldVisibilityToggle('show_how_heard')}
              />
            </div>

            <div className="flex items-center justify-between p-4 bg-muted/10 rounded-lg border border-border/50">
              <div>
                <Label className="font-medium">SMS Consent</Label>
                <p className="text-xs text-muted-foreground mt-1">Collect permission for SMS communications</p>
              </div>
              <Switch
                checked={editing.draft?.configuration?.show_sms_consent !== false}
                onCheckedChange={() => handleFieldVisibilityToggle('show_sms_consent')}
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* Custom Data Fields Section */}
        <div className="space-y-5">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
              <MessageSquare className="w-5 h-5" />
              Custom Data Fields
              {customQuestionsCount > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-primary/20 text-primary text-xs rounded-full font-normal">
                  {customQuestionsCount}
                </span>
              )}
            </h3>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              Register additional data points to collect beyond the standard fields. Each custom field extends the booking data schema and will be validated and stored by the API.
            </p>
          </div>

          <div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Label</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Options</TableHead>
                  <TableHead className="w-24">Required</TableHead>
                  <TableHead>Validation</TableHead>
                  <TableHead className="w-24">Step</TableHead>
                  <TableHead className="w-20 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Existing Questions */}
                {(editing.draft?.configuration?.custom_questions || []).map((question) => (
                  <TableRow key={question.id} className="group">
                    <TableCell className="font-medium">{question.label}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {getQuestionTypeLabel(question.type)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatOptions(question)}
                    </TableCell>
                    <TableCell className="text-sm">
                      {question.required ? (
                        <Check className="w-4 h-4 text-emerald-500" />
                      ) : (
                        <X className="w-4 h-4 text-muted-foreground" />
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatValidation(question)}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={question.show_on_step === 'service' ? 'default' : 'secondary'}
                        className="text-xs"
                      >
                        {getStepLabel(question.show_on_step)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditQuestion(question)}
                          className="h-7 w-7 p-0 hover:bg-zinc-700/50"
                        >
                          <Edit3 className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteQuestion(question.id)}
                          className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}

                {/* Empty State Row */}
                {customQuestionsCount === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12">
                      <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                      <p className="text-sm text-muted-foreground mb-4">
                        No custom fields registered
                      </p>
                      <Button size="sm" variant="outline" onClick={handleAddQuestion}>
                        <Plus className="w-4 h-4 mr-2" />
                        Add Custom Field
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {/* Add Button Below Table */}
            {customQuestionsCount > 0 && (
              <div className="mt-4">
                <Button size="sm" variant="outline" onClick={handleAddQuestion}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Custom Field
                </Button>
              </div>
            )}
          </div>
        </div>

        <Separator />

        {/* API Information Section */}
        <div className="space-y-5">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
              <Code className="w-5 h-5" />
              API Information
            </h3>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              Use these credentials to integrate this widget with your custom application. Keep your API key secure.
            </p>
          </div>

          {/* Widget ID */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Widget ID</Label>
            <div className="flex gap-2">
              <Input
                value={selectedWidget.id}
                readOnly
                className="font-mono text-sm bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)]"
              />
              <Button
                size="icon"
                variant="outline"
                onClick={() => copyToClipboard(selectedWidget.id, setCopiedWidgetId)}
                className="shrink-0"
              >
                {copiedWidgetId ? (
                  <Check className="w-4 h-4 text-green-500" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>

          {/* Business API Key */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Business API Key</Label>
            <div className="flex gap-2">
              <Input
                value={selectedWidget.business?.api_key || 'Loading...'}
                readOnly
                className="font-mono text-sm bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)]"
              />
              <Button
                size="icon"
                variant="outline"
                onClick={() => copyToClipboard(selectedWidget.business?.api_key || '', setCopiedApiKey)}
                className="shrink-0"
              >
                {copiedApiKey ? (
                  <Check className="w-4 h-4 text-green-500" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              This key is required for all API requests
            </p>
          </div>

          {/* API Endpoints */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">API Endpoints</Label>
            <div className="p-4 bg-zinc-900 rounded-lg border border-zinc-700/50 font-mono text-xs space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">POST</span>
                <span>/api/v2/public/widget/{'{widget_id}'}/config</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">POST</span>
                <span>/api/v2/public/services/</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">POST</span>
                <span>/api/v2/public/availability/</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">POST</span>
                <span>/api/v2/public/appointments/</span>
              </div>
            </div>
          </div>

          {/* Field Names Documentation */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Required Field Names</Label>
            <p className="text-xs text-muted-foreground mb-3">
              Use these exact field names when submitting booking data to the API
            </p>
            <div className="p-4 bg-zinc-900 rounded-lg border border-zinc-700/50 font-mono text-xs space-y-3">
              {/* Required Fields */}
              <div>
                <p className="text-emerald-400 mb-2">// Required fields (always include)</p>
                <div className="space-y-1 pl-2">
                  <div><span className="text-sky-400">customer_name</span>: <span className="text-orange-300">"string"</span></div>
                  <div><span className="text-sky-400">customer_email</span>: <span className="text-orange-300">"string"</span></div>
                  <div><span className="text-sky-400">customer_phone</span>: <span className="text-orange-300">"string"</span></div>
                </div>
              </div>

              {/* Optional Fields */}
              {(editing.draft?.configuration?.show_address || editing.draft?.configuration?.show_notes !== false || editing.draft?.configuration?.show_how_heard !== false || editing.draft?.configuration?.show_sms_consent !== false) && (
                <div>
                  <p className="text-emerald-400 mb-2">// Optional fields (based on your config)</p>
                  <div className="space-y-1 pl-2">
                    {editing.draft?.configuration?.show_address && (
                      <div><span className="text-sky-400">service_address</span>: <span className="text-orange-300">"string"</span></div>
                    )}
                    {editing.draft?.configuration?.show_notes !== false && (
                      <div><span className="text-sky-400">notes</span>: <span className="text-orange-300">"string"</span></div>
                    )}
                    {editing.draft?.configuration?.show_how_heard !== false && (
                      <div><span className="text-sky-400">how_heard</span>: <span className="text-orange-300">"string"</span></div>
                    )}
                    {editing.draft?.configuration?.show_sms_consent !== false && (
                      <div><span className="text-sky-400">sms_consent</span>: <span className="text-orange-300">"boolean"</span></div>
                    )}
                  </div>
                </div>
              )}

              {/* Custom Fields */}
              {customQuestionsCount > 0 && (
                <div>
                  <p className="text-emerald-400 mb-2">// Custom fields</p>
                  <div className="space-y-1 pl-2">
                    {(editing.draft?.configuration?.custom_questions || []).map((question, index) => (
                      <div key={index}>
                        <span className="text-sky-400">{question.id || `custom_field_${index + 1}`}</span>: <span className="text-orange-300">"{question.type}"</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* Question Form Dialog */}
      <QuestionFormDialog
        isOpen={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
          setEditingQuestion(null);
        }}
        onSave={handleSaveQuestion}
        initialData={editingQuestion}
      />
    </div>
  );
};

export default HeadlessWidgetEditor;
